package data

import kotlinx.browser.window
import kotlinx.coroutines.await

// ───────────────── CSV 파서 ─────────────────

private fun splitCsvLine(line: String): List<String> {
    val out = mutableListOf<String>()
    val sb = StringBuilder()
    var inQuotes = false
    var i = 0
    while (i < line.length) {
        when (val ch = line[i]) {
            '"' -> {
                if (inQuotes && i + 1 < line.length && line[i + 1] == '"') { sb.append('"'); i++ }
                else inQuotes = !inQuotes
            }
            ',' -> if (inQuotes) sb.append(ch) else { out.add(sb.toString()); sb.clear() }
            else -> sb.append(ch)
        }
        i++
    }
    out.add(sb.toString())
    return out
}

private fun parseCsv(csv: String): List<Map<String, String>> {
    val lines = csv.lines().filter { it.isNotBlank() }
    if (lines.isEmpty()) return emptyList()
    val header = splitCsvLine(lines.first()).map { it.trim() }
    return lines.drop(1).map { line ->
        val cells = splitCsvLine(line)
        header.zip(cells + List(maxOf(0, header.size - cells.size)) { "" })
            .associate { (h, v) -> h.trim() to v.trim() }
    }
}

// ─────────────── 유틸 파서들 ────────────────

private fun parseReliability(raw: String?): Double {
    val s = (raw ?: "").trim().lowercase()
    if (s.isBlank()) return 1.0
    s.toDoubleOrNull()?.let { return it.coerceIn(0.5, 5.0) }
    return when (s) {
        "high", "gov", "medical" -> 3.0
        "medium", "mid", "news"  -> 2.0
        "low", "blog"            -> 1.0
        else -> 1.0
    }
}

private fun isTrue(raw: String?): Boolean {
    val s = (raw ?: "").trim().lowercase()
    return s in setOf("true","1","y","yes","있음")
}

private fun normalizedVerdict(v: String?): String? = when (v?.trim()?.lowercase()) {
    "avoid","피해야 함","피함" -> "avoid"
    "caution","주의"          -> "caution"
    "conditional","조건부"     -> "conditional"
    "safe","안전","none","없음" -> "safe"
    else -> null
}

private fun verdictRank(v: String?): Int = when (v) {
    "avoid" -> 3 // 가장 강한 경고
    "caution" -> 2
    "conditional" -> 1
    "safe" -> 0 // 가장 약한 경고 (안전)
    else -> -1
}

// ────── 메인 로더: foods.csv → List<FoodSafetySummary> ──────

suspend fun loadFoodsFromCsv(path: String = "foods.csv"): List<FoodSafetySummary> {
    val response = window.fetch(path).await()
    val text = response.text().await()
    val rows = parseCsv(text)

    // CSV 컬럼명 (필요시 너의 CSV에 맞게 조정)
    val FOOD = "food_kr"                 // 음식 한글명(그룹 키)
    val REL  = "source_reliability"      // 신뢰도(숫자/라벨)
    val SRC  = "source_name"             // 출처 표시용 이름
    val URL  = "rule_source"             // 출처 URL
    val P_HAS= "임산부_정보유무"           // TRUE/FALSE
    val P_V  = "임산부_주의"               // avoid/caution/conditional/safe (한글도 허용)

    // 1) 음식별 그룹핑
    val grouped = rows.groupBy { it[FOOD].orEmpty() }.filterKeys { it.isNotBlank() }

    return grouped.map { (foodName, items) ->

        // 2) 이 음식에 해당하는 로우 → 유효 엔트리로 변환
        val allEntries = items.mapNotNull { r ->
            if (!isTrue(r[P_HAS])) return@mapNotNull null
            val verdict = normalizedVerdict(r[P_V]) ?: return@mapNotNull null

            FoodSafetyEntry(
                source = r[SRC].orEmpty(),
                verdict = verdict,                         // "avoid"/"caution"/"conditional"/"safe"
                reliability = parseReliability(r[REL]),    // Double
                note = r[P_V],                             // 원문 라벨 보존
                url = r[URL]
            )
        }

        // 3) ✅ 같은 URL(없으면 source)로 묶어서 “가장 강한 경고 + 신뢰도 높은” 하나만 남김
        // 그룹 키: URL이 있으면 URL, 없으면 source name을 사용
        val deduped = allEntries
            .groupBy { (it.url?.takeIf { u -> u.isNotBlank() } ?: it.source).trim() }
            .values
            .map { group ->
                // 그룹 내에서 가장 강한 경고(verdictRank 값이 높을수록 강함)를 선택하고
                // 경고가 같으면 신뢰도(reliability)가 높은 항목을 선택
                group.maxWith(
                    compareBy<FoodSafetyEntry> { verdictRank(it.verdict) }
                        .thenByDescending { it.reliability }
                )
            }

        FoodSafetySummary(
            name = foodName,
            entries = deduped.sortedByDescending { it.reliability } // 보기 좋게 정렬
        )
    }.sortedBy { it.name }
}

// ────── synonyms.csv → Map<String, List<String>> ──────
suspend fun loadSynonymsFromCsv(path: String = "synonyms.csv"): Map<String, List<String>> {
    val response = window.fetch(path).await()
    val text = response.text().await()

    val lines = text.lines().filter { it.isNotBlank() }
    if (lines.isEmpty()) return emptyMap()

    // header: key,aliases
    val header = splitCsvLine(lines.first()).map { it.trim() }
    val keyIdx = header.indexOf("key")
    val aliasIdx = header.indexOf("aliases")
    if (keyIdx < 0 || aliasIdx < 0) return emptyMap()

    val map = mutableMapOf<String, List<String>>()

    for (line in lines.drop(1)) {
        val cells = splitCsvLine(line)
        if (cells.size <= maxOf(keyIdx, aliasIdx)) continue

        val key = cells[keyIdx].trim()
        val aliasesRaw = cells[aliasIdx].trim().trim('"')

        if (key.isBlank()) continue

        val aliases = aliasesRaw
            .split("|")
            .map { it.trim() }
            .filter { it.isNotBlank() && it != key }

        map[key] = aliases
    }

    return map
}

//-----------token--------------------//
// ✅ DB 기반 의미 토큰(1~4글자) 만들기: foods.csv에 실제로 존재하는 짧은 이름만
fun buildMeaningfulTokens1to4(summaries: List<FoodSafetySummary>): Set<String> =
    summaries
        .map { it.name.trim() }
        .filter { it.isNotBlank() }
        .filter { it.length in 1..4 }
        .filter { it.all { ch -> ch in '\uAC00'..'\uD7A3' } } // 한글만 (원하면 완화 가능)
        .toSet()

// ✅ 쿼리를 1~4 substring으로 쪼개되, DB에 있는 토큰만 힌트로 사용
fun extractQueryHintsFromDb(query: String, meaningful: Set<String>): Set<String> {
    val q = query.trim().lowercase().replace(Regex("\\s+"), "")
    if (q.isEmpty()) return emptySet()

    val out = mutableSetOf<String>()
    val L = q.length

    for (n in 1..4) {
        if (L < n) continue
        for (i in 0..(L - n)) {
            val sub = q.substring(i, i + n)
            if (sub in meaningful) out.add(sub)
        }
    }

    // ✅ 2글자 이상 힌트가 있으면 1글자도 허용(팥빙수→팥 같은 케이스)
    val has2plus = out.any { it.length >= 2 }
    return if (has2plus) out else out.filter { it.length == 1 }.toSet()
}